import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowRight, ShieldCheck, Home as HomeIcon, Handshake, Building2, Rocket, LogIn, UserPlus, Check, X, Sparkles, PhoneCall, BadgeCheck, PlusCircle, LocateFixed, Loader2, ChevronsUp, PlayCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import RentEstimator from "@/components/RentEstimator";
import AiSearchBar from "@/components/AiSearchBar";
import SEO from "@/components/SEO";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

// Iconic landmark photos per Indian city — searched by lowercase key
const CITY_HERO_IMAGES = {
  delhi:     { img: "https://images.unsplash.com/photo-1587474260584-136574528ed5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "India Gate" },
  bengaluru: { img: "https://images.unsplash.com/photo-1596176530529-78163a4f7af2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Vidhana Soudha" },
  bangalore: { img: "https://images.unsplash.com/photo-1596176530529-78163a4f7af2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Vidhana Soudha" },
  mumbai:    { img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Gateway of India" },
  jodhpur:   { img: "https://images.unsplash.com/photo-1669548329838-45008f9a45fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Mehrangarh Fort & Umaid Bhawan" },
  jaipur:    { img: "https://images.unsplash.com/photo-1599661046289-e31897846e41?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Amer Fort & Hawa Mahal" },
  hyderabad: { img: "https://images.unsplash.com/photo-1741545979534-02f59c742730?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Charminar" },
  chennai:   { img: "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Kapaleeshwarar Temple" },
  kolkata:   { img: "https://images.unsplash.com/photo-1558431382-27e303142255?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Victoria Memorial" },
  pune:      { img: "https://images.unsplash.com/photo-1705955463252-e3f670e4041b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "City Skyline" },
  gurgaon:   { img: "https://images.unsplash.com/photo-1697135376181-f6b7aac6caf6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Cyber City skyline" },
  gurugram:  { img: "https://images.unsplash.com/photo-1697135376181-f6b7aac6caf6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Cyber City skyline" },
  agra:      { img: "https://images.unsplash.com/photo-1548013146-72479768bada?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Taj Mahal" },
  varanasi:  { img: "https://images.unsplash.com/photo-1706186839147-0d708602587b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Ganga Ghats" },
  udaipur:   { img: "https://images.unsplash.com/photo-1695956353120-54ce5e91632b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Lake Palace" },
  goa:       { img: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Palolem Beach" },
  ahmedabad: { img: "https://images.unsplash.com/photo-1687840430404-b82859282de4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", landmark: "Sabarmati Riverfront" },
};

// De-duplicated spotlight rotation (skip alias keys like bangalore/gurugram)
const SPOTLIGHT_CITIES = [
  "jodhpur", "jaipur", "udaipur", "mumbai", "bengaluru", "delhi",
  "agra", "varanasi", "goa", "kolkata", "hyderabad", "chennai",
  "pune", "gurgaon", "ahmedabad",
].map((key) => ({ key, name: key.charAt(0).toUpperCase() + key.slice(1), ...CITY_HERO_IMAGES[key] }));

const POPULAR_CITIES = [
  { name: "Bengaluru", sub: "Karnataka" },
  { name: "Mumbai", sub: "Maharashtra" },
  { name: "Delhi", sub: "NCR" },
  { name: "Pune", sub: "Maharashtra" },
  { name: "Hyderabad", sub: "Telangana" },
  { name: "Jodhpur", sub: "Rajasthan" },
  { name: "Chennai", sub: "Tamil Nadu" },
  { name: "Gurgaon", sub: "Haryana" },
];

/**
 * HeroSection — cinematic 3D parallax hero.
 * The landmark image subtly tilts and pans based on mouse position,
 * giving the feeling that the visitor is looking into a window.
 */
function HeroSection({ heroImage, activeMeta, defaultHero, children }) {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const smx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });
  const tx = useTransform(smx, [0, 1], ["3%", "-3%"]);
  const ty = useTransform(smy, [0, 1], ["3%", "-3%"]);
  const rx = useTransform(smy, [0, 1], ["2deg", "-2deg"]);
  const ry = useTransform(smx, [0, 1], ["-2deg", "2deg"]);
  const scale = 1.08; // subtle over-scale so the parallax edges stay covered

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      onMouseMove={onMouseMove}
      className="relative min-h-[86vh] flex items-end overflow-hidden"
      style={{ perspective: "1200px" }}
      data-testid="hero-section"
    >
      <motion.img
        key={heroImage}
        src={heroImage}
        alt={`${activeMeta.name} — ${activeMeta.landmark}`}
        data-testid="hero-image"
        style={{ x: tx, y: ty, rotateX: rx, rotateY: ry, scale }}
        className="absolute inset-0 w-full h-full object-cover fade-in will-change-transform"
        onError={(e) => { if (e.currentTarget.src !== defaultHero) e.currentTarget.src = defaultHero; }}
      />
      <div className="absolute inset-0 hero-scrim" />
      <div className="absolute inset-0 hero-spotlight pointer-events-none" />
      {children}
    </section>
  );
}

export default function HomePage() {
  const nav = useNavigate();
  const { config, user } = useAuth();
  const boostOn = !!config?.boost_enabled;
  const authed = user && user !== false;
  const [tab, setTab] = useState("rent");
  const [city, setCity] = useState("");
  const [locality, setLocality] = useState("");
  const [featured, setFeatured] = useState([]);
  const [spotlightIdx, setSpotlightIdx] = useState(() => Math.floor(Math.random() * SPOTLIGHT_CITIES.length));
  const [detecting, setDetecting] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [cityCounts, setCityCounts] = useState({});

  const goToCity = (cityName) => {
    const p = new URLSearchParams();
    p.set("listing_type", tab);
    p.set("city", cityName);
    nav(`/properties?${p.toString()}`);
  };

  const detectLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Your browser does not support location detection.");
      setShowCityPicker(true);
      return;
    }
    setDetecting(true);
    toast.info("Requesting your location…");
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.latitude}&longitude=${coords.longitude}&localityLanguage=en`
          );
          const data = await res.json();
          const detectedCity = (data.city || data.locality || data.principalSubdivision || "").trim();
          if (!detectedCity) {
            toast.error("Could not identify your city. Please pick one below.");
            setShowCityPicker(true);
          } else {
            setCity(detectedCity);
            toast.success(`Detected: ${detectedCity} — showing nearby listings`);
            const p = new URLSearchParams();
            p.set("listing_type", tab);
            p.set("city", detectedCity);
            nav(`/properties?${p.toString()}`);
          }
        } catch {
          toast.error("Location lookup failed. Please pick your city below.");
          setShowCityPicker(true);
        } finally {
          setDetecting(false);
        }
      },
      (err) => {
        setDetecting(false);
        if (err.code === 1) toast.error("Location denied — pick your city below to continue.");
        else if (err.code === 3) toast.error("Location request timed out. Pick your city below.");
        else toast.error("Could not get your location. Pick your city below.");
        setShowCityPicker(true);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  const cityKey = city.trim().toLowerCase();
  const typedMeta = CITY_HERO_IMAGES[cityKey];
  const spotlight = SPOTLIGHT_CITIES[spotlightIdx];
  // When the user hasn't typed a matching city, rotate a random Indian city spotlight
  const activeMeta = typedMeta
    ? { name: city, landmark: typedMeta.landmark, img: typedMeta.img }
    : { name: spotlight.name, landmark: spotlight.landmark, img: spotlight.img };
  const heroImage = activeMeta.img;

  useEffect(() => {
    api.get("/properties", { params: { limit: 6 } }).then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/cities/counts").then(({ data }) => setCityCounts(data || {})).catch(() => {});
  }, []);

  // Rotate spotlight every 6s while user hasn't typed a matching city
  useEffect(() => {
    if (typedMeta) return; // frozen when user actively searching a known city
    const t = setInterval(() => {
      setSpotlightIdx((i) => (i + 1) % SPOTLIGHT_CITIES.length);
    }, 6000);
    return () => clearInterval(t);
  }, [typedMeta]);

  const search = () => {
    const p = new URLSearchParams();
    p.set("listing_type", tab);
    if (city) p.set("city", city);
    if (locality) p.set("locality", locality);
    nav(`/properties?${p.toString()}`);
  };

  return (
    <div>
      <SEO
        title="Rent or buy homes directly from verified owners"
        description="Skip the brokers. To-Let Rentals connects renters and buyers directly with property owners in Bengaluru, Mumbai, Delhi, Pune, Hyderabad and 10+ Indian cities. Zero brokerage. Free forever. AI rent estimator included."
        path="/"
        keywords="rent house India, flats for rent, no brokerage rentals, owner listings, 2BHK rent, apartments for sale, To-Let Rentals, Bengaluru rent, Mumbai flats, Delhi rentals, Pune apartments, Hyderabad PG"
      />
      {/* HERO */}
      <HeroSection
        heroImage={heroImage}
        activeMeta={activeMeta}
        defaultHero={DEFAULT_HERO}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-32 w-full text-white fade-in">
          <p className="label-eyebrow mb-6 text-[#C9A24A]">Find homes near you</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-3xl mb-6 text-white">
            Rent or buy — <em className="text-[#C9A24A] not-italic italic">locally</em>,<br/>from real owners.
          </h1>
          <p className="text-lg text-white/85 max-w-xl mb-6 leading-relaxed">
            Skip the brokers. To-Let Rentals connects renters and buyers directly with property owners in their neighborhood.
          </p>

          {/* AI search entry — an addition, not a replacement for filters below */}
          <div className="max-w-3xl mb-6">
            <div className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full bg-[#C9A24A]/20 border border-[#C9A24A]/40 text-[#F3E4BC] text-[10px] font-bold tracking-widest uppercase">
              <Sparkles size={11} /> New · Search with AI
            </div>
            <AiSearchBar variant="hero" showExamples={false} />
          </div>

          {/* Search pill — royal white on hero */}
          <div className="rounded-3xl p-2.5 bg-white border border-[#EFE3D0] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.4)] max-w-4xl flex flex-col md:flex-row gap-2 items-stretch">
            <div className="flex bg-[#FBF6EC] rounded-2xl p-1 border border-[#EFE3D0]">
              {["rent", "sale"].map((t) => (
                <button
                  key={t}
                  data-testid={`tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold capitalize transition-colors ${
                    tab === t ? "bg-[#7A1E2A] text-white shadow-[0_6px_16px_-6px_rgba(122,30,42,0.5)]" : "text-gray-600 hover:text-[#7A1E2A]"
                  }`}
                >
                  {t === "rent" ? "Rent" : "Buy"}
                </button>
              ))}
            </div>
            <div className="flex-1 flex items-center px-4 gap-2 border-l border-[#EFE3D0]">
              <MapPin size={16} className="text-gray-400" />
              <Input
                data-testid="search-city"
                value={city} onChange={(e) => setCity(e.target.value)}
                placeholder="City (e.g. Bengaluru)"
                className="border-0 bg-transparent focus-visible:ring-0 h-11 text-base text-gray-900 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={detectLocation}
                disabled={detecting}
                data-testid="detect-location-btn"
                title="Use my current location"
                aria-label="Use my current location"
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#7A1E2A] hover:text-[#5C1420] disabled:opacity-60 disabled:cursor-not-allowed transition-colors pr-1"
              >
                {detecting ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                <span className="hidden sm:inline">{detecting ? "Detecting…" : "Detect"}</span>
              </button>
            </div>
            <div className="flex-1 flex items-center px-4 gap-2 border-l border-[#EFE3D0]">
              <Search size={16} className="text-gray-400" />
              <Input
                data-testid="search-locality"
                value={locality} onChange={(e) => setLocality(e.target.value)}
                placeholder="Locality or landmark"
                className="border-0 bg-transparent focus-visible:ring-0 h-11 text-base text-gray-900 placeholder:text-gray-400"
              />
            </div>
            <Button
              data-testid="search-button"
              onClick={search}
              className="tlr-gold-btn rounded-2xl h-12 px-8"
            >
              Search <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>

          {/* Popular city chips */}
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="city-chips">
            <span className="text-xs text-white/70 uppercase tracking-widest mr-1 hidden md:inline">Popular:</span>
            <button
              type="button"
              onClick={detectLocation}
              disabled={detecting}
              data-testid="near-me-chip"
              className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full tlr-gold-btn text-sm disabled:opacity-70"
              title="Detect my current city"
            >
              {detecting ? <Loader2 size={13} className="animate-spin" /> : <LocateFixed size={13} />}
              {detecting ? "Detecting…" : "Near me"}
            </button>
            {POPULAR_CITIES.map((c) => {
              const cnt = cityCounts[c.name.toLowerCase()] || 0;
              return (
                <button
                  key={c.name}
                  data-testid={`city-chip-${c.name.toLowerCase()}`}
                  onClick={() => goToCity(c.name)}
                  className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/12 hover:bg-white backdrop-blur-sm text-white hover:text-[#7A1E2A] border border-white/25 hover:border-[#C9A24A] text-sm font-medium transition-colors"
                >
                  <Building2 size={13} className="opacity-70 group-hover:opacity-100 group-hover:text-[#C9A24A]" />
                  {c.name}
                  {cnt > 0 && (
                    <span
                      data-testid={`city-count-${c.name.toLowerCase()}`}
                      className="text-[10px] font-bold bg-[#C9A24A] text-[#1B0F14] rounded-full px-1.5 py-0.5 group-hover:bg-[#7A1E2A] group-hover:text-white transition-colors"
                    >
                      {cnt}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Manual city picker – shown after failed/denied geolocation */}
          {showCityPicker && (
            <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="city-picker-fallback">
              <span className="text-xs text-white/80 tracking-widest uppercase">Pick your city:</span>
              <div className="min-w-[220px] bg-white rounded-full pl-2 pr-1 py-1 flex items-center shadow-lg">
                <MapPin size={14} className="text-[#FF5A3D] ml-2 mr-1" />
                <Select
                  value=""
                  onValueChange={(v) => { setShowCityPicker(false); goToCity(v); }}
                >
                  <SelectTrigger data-testid="manual-city-select" className="border-0 bg-transparent focus:ring-0 h-9 text-gray-900">
                    <SelectValue placeholder="Choose a city…" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPOTLIGHT_CITIES.map((c) => (
                      <SelectItem key={c.key} value={c.name} data-testid={`picker-option-${c.key}`}>
                        <span className="capitalize">{c.name}</span>
                        {cityCounts[c.key] ? (
                          <span className="ml-2 text-xs text-gray-500">· {cityCounts[c.key]} listing{cityCounts[c.key] > 1 ? "s" : ""}</span>
                        ) : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={() => setShowCityPicker(false)}
                className="text-xs text-white/70 hover:text-white underline"
                data-testid="dismiss-city-picker"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </HeroSection>

      {/* AI RENT ESTIMATOR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16">
        <RentEstimator />
      </section>

      {/* DISCOVER / SCROLL TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16" data-testid="discover-teaser">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A0B10] via-[#3D0D15] to-[#5C1420] p-8 md:p-12 text-white border border-[#C9A24A]/25">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C9A24A]/20 blur-3xl" />
          <div className="relative grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 bg-[#C9A24A]/20 border border-[#C9A24A]/40 text-[#C9A24A] px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase mb-4">
                <ChevronsUp size={13} /> Just launched
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-4">
                Scroll homes like it's <em className="not-italic text-[#C9A24A]">Instagram</em>.
              </h2>
              <p className="text-white/85 leading-relaxed max-w-lg mb-6">
                Tired of scrolling endless listings? On <b>Discover</b>, you just swipe up to see the next home walkthrough. Tap the heart to save your favourite, tap the card to reveal the owner.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="discover-cta"
                  onClick={() => nav("/discover")}
                  className="tlr-gold-btn rounded-full h-12 px-7"
                >
                  <PlayCircle size={16} className="mr-2" /> Start scrolling
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => nav("/properties")}
                  className="rounded-full h-12 px-6 text-white/85 hover:text-white hover:bg-white/8 border border-white/15"
                >
                  Or browse the grid
                </Button>
              </div>
            </div>
            <div className="md:col-span-2 hidden md:flex justify-center">
              <div className="relative w-56 h-96 rounded-[2rem] bg-black border-2 border-[#C9A24A]/40 overflow-hidden shadow-[0_30px_80px_-20px_rgba(201,162,74,0.35)]">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />
                <img
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&auto=format&q=80"
                  alt="Discover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="absolute inset-x-0 bottom-0 p-4 z-10">
                  <p className="text-[10px] uppercase tracking-widest text-[#C9A24A] font-bold mb-1">For Rent</p>
                  <p className="font-display text-lg text-white leading-tight">2 BHK · Indiranagar</p>
                  <p className="text-[#C9A24A] text-sm font-semibold mt-0.5">₹32,000/mo</p>
                </div>
                {/* swipe-up prompt */}
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none">
                  <div className="animate-bounce">
                    <ChevronsUp size={28} className="text-[#C9A24A]" />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-white/70 mt-1">Swipe</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOLD FREE LISTING CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-4" data-testid="free-listing-cta">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#7A1E2A] via-[#5C1420] to-[#3D0D15] p-8 md:p-16 text-white border border-[#C9A24A]/25">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#C9A24A]/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#C9A24A]/12 blur-3xl" />
          <div className="relative grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 bg-[#C9A24A]/20 backdrop-blur px-4 py-2 rounded-full mb-4 border border-[#C9A24A]/40">
                <Sparkles size={14} className="text-[#C9A24A]" />
                <span className="text-xs font-bold tracking-widest uppercase text-[#C9A24A]">Owners — Read this</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4 font-semibold">
                List your property. <em className="not-italic text-[#C9A24A]">Absolutely free.</em>
              </h2>
              <p className="text-lg md:text-xl text-white/90 max-w-xl leading-relaxed mb-8">
                Zero listing fee. Zero brokerage. Zero commission. Post your rental or sale in 90 seconds and reach local renters directly — forever.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  data-testid="free-list-cta-btn"
                  onClick={() => {
                    // GTM top-of-funnel intent event — same event name the
                    // /owners landing page fires so both entry points feed
                    // the Google Ads conversion action.
                    try {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "post_listing_clicked",
                        cta_source: "homepage_owner_banner",
                        page: "homepage",
                      });
                    } catch (_) { /* noop */ }
                    nav("/list-property");
                  }}
                  className="bg-white text-[#FF5A3D] hover:bg-[#FFD166] hover:text-[#1A1523] rounded-full h-14 px-8 text-base font-bold shadow-xl"
                >
                  <PlusCircle size={18} className="mr-2" /> Post FREE Listing
                </Button>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <BadgeCheck size={16} className="text-[#FFD166]" />
                  <span>Verified owners get instant approval</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-3">
              {[
                { big: "₹0", small: "Listing fee" },
                { big: "₹0", small: "Brokerage" },
                { big: "90s", small: "To go live" },
                { big: "Direct", small: "Owner contact" },
              ].map((s) => (
                <div key={s.small} className="bg-white/15 backdrop-blur rounded-2xl p-5 border border-white/20">
                  <p className="font-display text-3xl font-bold">{s.big}</p>
                  <p className="text-xs text-white/75 uppercase tracking-widest mt-1">{s.small}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US — comparison table (99acres/NoBrokers style trust section) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center mb-12">
          <p className="label-eyebrow text-gray-500 mb-3">The direct-to-owner way</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl brand-forest mb-3 tracking-tight">
            Why choose <em className="not-italic text-[#0EA5E9]">To-Let Rentals</em>?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            A cleaner, cheaper way to rent and buy homes locally — with none of the traditional broker fees or fake listings.
          </p>
        </div>

        <div className="overflow-hidden rounded-3xl border border-brand-subtle bg-white">
          <div className="grid grid-cols-3 gap-0 divide-x divide-brand-subtle">
            <div className="p-6 text-center">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Feature</p>
            </div>
            <div className="p-6 text-center bg-[#FFEED9]">
              <p className="font-display text-xl brand-forest font-bold">To-Let Rentals</p>
              <p className="text-xs text-[#FF5A3D] mt-1 font-semibold uppercase tracking-widest">You save money</p>
            </div>
            <div className="p-6 text-center">
              <p className="font-display text-xl text-gray-600 font-semibold">Traditional Brokers</p>
              <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest">The old way</p>
            </div>
          </div>
          {[
            ["Brokerage fee",       "₹0",                    "1-2 months' rent"],
            ["Listing fee",         "Free",                  "₹1,500 – ₹5,000"],
            ["Contact owner",       "Direct — 10 free",      "Via middleman only"],
            ["Verified landlords",  "Yes, ID-verified",      "Rarely"],
            ["Response time",       "Instant call / WhatsApp","Days"],
            ["Site visits",         "Book directly",         "Broker-scheduled"],
            ["Rejected listings",   "Admin-reviewed",        "Anyone can post"],
          ].map((row) => (
            <div key={row[0]} className="grid grid-cols-3 gap-0 divide-x divide-brand-subtle border-t border-brand-subtle">
              <div className="p-5 flex items-center font-semibold text-gray-800 text-sm">{row[0]}</div>
              <div className="p-5 flex items-center gap-2 bg-[#FFF6E9]">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <Check size={14} className="text-emerald-600" />
                </div>
                <span className="font-semibold brand-forest text-sm">{row[1]}</span>
              </div>
              <div className="p-5 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <X size={14} className="text-red-500" />
                </div>
                <span className="text-gray-600 text-sm">{row[2]}</span>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* SIGN UP CTA — for logged-out visitors */}
      {!authed && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-4" data-testid="auth-cta-section">
          <div className="relative overflow-hidden rounded-3xl bg-white border-2 border-[#FF5A3D] p-8 md:p-12 sunburst">
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="w-16 h-16 rounded-2xl bg-[#FFEED9] flex items-center justify-center shrink-0">
                <UserPlus size={30} className="brand-forest" />
              </div>
              <div className="flex-1">
                <p className="label-eyebrow text-gray-500 mb-2">New to To-Let Rentals?</p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight brand-forest mb-3">
                  Create a free account to save homes and contact owners.
                </h2>
                <p className="text-gray-600 max-w-2xl">
                  Sign up in seconds — save your favorite listings, reveal <b>10 owner contacts for free</b>, and message landlords directly. Already have an account? Just log in.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="home-signup-cta"
                  onClick={() => nav("/register")}
                  className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-12 px-6 font-semibold"
                >
                  <UserPlus size={16} className="mr-2" /> Sign up free
                </Button>
                <Button
                  data-testid="home-login-cta"
                  onClick={() => nav("/login")}
                  variant="outline"
                  className="rounded-full h-12 px-6 border-2 border-brand-subtle text-[#FF5A3D] hover:bg-[#FFEED9]"
                >
                  <LogIn size={16} className="mr-2" /> Log in
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* BOOST CTA — prominent placement for owners */}
      {boostOn && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-4">
        <div className="relative overflow-hidden rounded-3xl bg-brand-forest text-white p-8 md:p-12">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FFD166]/25 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 w-72 h-72 rounded-full bg-[#0EA5E9]/25 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <Rocket size={30} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="label-eyebrow text-white/70 mb-2">Own a property?</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3">
                Boost your listing to the top from just <em className="not-italic text-[#FFD166]">₹149</em>.
              </h2>
              <p className="text-white/85 max-w-2xl">
                Featured listings get 5× more views. Pick a 7, 14 or 30-day boost — one click, one payment, straight to the top of search.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="home-boost-cta"
                onClick={() => nav("/boost")}
                className="bg-white text-[#FF5A3D] hover:bg-[#FFD166] hover:text-[#1A1523] rounded-full h-12 px-6 font-semibold"
              >
                <Rocket size={16} className="mr-2" /> Boost my property
              </Button>
              <Button
                onClick={() => nav("/list-property")}
                variant="outline"
                className="rounded-full h-12 px-6 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white"
              >
                <ArrowRight size={16} className="mr-2" /> List a property
              </Button>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: <HomeIcon size={22} />, title: "Local listings", body: "Filter by city and locality — see homes right in your area." },
            { icon: <Handshake size={22} />, title: "Direct to owner", body: "Chat with the landlord themselves. No middlemen, no brokerage." },
            { icon: <ShieldCheck size={22} />, title: "10 free contacts", body: "Reveal 10 owner contacts free. Upgrade only when you're serious." },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-8 border border-brand-subtle hover-lift">
              <div className="w-11 h-11 rounded-xl bg-[#FFEED9] flex items-center justify-center brand-forest mb-5">{f.icon}</div>
              <h3 className="font-display text-2xl mb-2 brand-forest">{f.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-eyebrow text-gray-500 mb-2">Newest homes</p>
            <h2 className="font-display text-4xl sm:text-5xl brand-forest">Freshly listed by owners</h2>
          </div>
          <Button
            onClick={() => nav("/properties")} variant="ghost"
            data-testid="view-all-btn"
            className="rounded-full brand-forest hidden sm:inline-flex"
          >
            View all <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
        {featured.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-brand-subtle">
            <p className="text-gray-500">No listings yet. Be the first to list your property!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
